import { preferenceStorage } from '@/utils/storage';
import { OFFLINE_QUEUE_KEY } from '@/mobile/constants/mobile';

export type OfflineQueueItem = {
  id: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  createdAt: number;
  retries: number;
};

async function readQueue(): Promise<OfflineQueueItem[]> {
  const raw = await preferenceStorage.getItem(OFFLINE_QUEUE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as OfflineQueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function writeQueue(items: OfflineQueueItem[]): Promise<void> {
  await preferenceStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(items));
}

export function createOfflineQueueItem(
  partial: Omit<OfflineQueueItem, 'id' | 'createdAt' | 'retries'>,
): OfflineQueueItem {
  return {
    ...partial,
    id: `oq_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    createdAt: Date.now(),
    retries: 0,
  };
}

export async function enqueueMutation(
  item: Omit<OfflineQueueItem, 'id' | 'createdAt' | 'retries'>,
): Promise<OfflineQueueItem> {
  const entry = createOfflineQueueItem(item);
  const queue = await readQueue();
  queue.push(entry);
  await writeQueue(queue);
  return entry;
}

export async function getOfflineQueue(): Promise<OfflineQueueItem[]> {
  return readQueue();
}

export async function removeFromQueue(id: string): Promise<void> {
  const queue = await readQueue();
  await writeQueue(queue.filter((item) => item.id !== id));
}

export async function clearOfflineQueue(): Promise<void> {
  await preferenceStorage.removeItem(OFFLINE_QUEUE_KEY);
}

export type FlushResult = {
  flushed: string[];
  failed: Array<{ id: string; error: string }>;
};

/**
 * Attempts to flush queued mutations. Caller provides the transport so tests
 * can inject a mock without axios.
 */
export async function flushOfflineQueue(
  send: (item: OfflineQueueItem) => Promise<void>,
  options?: { maxRetries?: number },
): Promise<FlushResult> {
  const maxRetries = options?.maxRetries ?? 3;
  const queue = await readQueue();
  const remaining: OfflineQueueItem[] = [];
  const flushed: string[] = [];
  const failed: Array<{ id: string; error: string }> = [];

  for (const item of queue) {
    try {
      await send(item);
      flushed.push(item.id);
    } catch (error) {
      const next = { ...item, retries: item.retries + 1 };
      const message = error instanceof Error ? error.message : 'Unknown error';
      if (next.retries >= maxRetries) {
        failed.push({ id: item.id, error: message });
      } else {
        remaining.push(next);
        failed.push({ id: item.id, error: message });
      }
    }
  }

  await writeQueue(remaining);
  return { flushed, failed };
}
